import { useEffect, useState, type RefObject } from "react";
import {
  Arc as KonvaArc,
  Ellipse as KonvaEllipse,
  Group as KonvaGroup,
  Image as KonvaImage,
  Layer,
  Line as KonvaLine,
  Path as KonvaPath,
  Rect as KonvaRect,
  RegularPolygon as KonvaRegularPolygon,
  Ring as KonvaRing,
  Stage,
  Star as KonvaStar,
  Text as KonvaText,
} from "react-konva";
import type { Stage as KonvaStage } from "konva/lib/Stage";
import {
  getSceneImageFitLayout,
  toKonvaImageFrameProps,
  toKonvaRectNodeProps,
  type SocialPostSceneLeafBlockV1,
  type SocialPostScenePlanV1,
} from "@runcheetah/shared";

import {
  getVisibleSortedSocialPostSceneBlocks,
  resolveSocialPostSceneLogoAsset,
  resolveSocialPostSceneTextNodeProps,
  type SocialPostSceneRendererBrandkit,
  type SocialPostSceneRendererLinkedAsset,
  type SocialPostSceneRendererLogoAsset,
} from "./social-post-scene-renderer-utils";

type SceneBlock = SocialPostSceneLeafBlockV1;

type SceneRenderContext = {
  scale: number;
  imageAssetByBlockId: ReadonlyMap<string, SocialPostSceneRendererLinkedAsset>;
  brandkit: SocialPostSceneRendererBrandkit | null;
  logoAssets: readonly SocialPostSceneRendererLogoAsset[];
  listening: boolean;
};

export type SocialPostSceneRendererProps = {
  plan: SocialPostScenePlanV1;
  linkedAssets: readonly SocialPostSceneRendererLinkedAsset[];
  brandkit: SocialPostSceneRendererBrandkit | null;
  logoAssets?: readonly SocialPostSceneRendererLogoAsset[];
  scale?: number;
  listening?: boolean;
  className?: string;
  stageRef?: RefObject<KonvaStage | null>;
};

export type SocialPostSceneBlockContentProps = {
  block: SceneBlock;
  scale?: number;
  imageAssetByBlockId: ReadonlyMap<string, SocialPostSceneRendererLinkedAsset>;
  brandkit: SocialPostSceneRendererBrandkit | null;
  logoAssets?: readonly SocialPostSceneRendererLogoAsset[];
  listening?: boolean;
};

export function SocialPostSceneRenderer({
  plan,
  linkedAssets,
  brandkit,
  logoAssets = [],
  scale = 1,
  listening = false,
  className,
  stageRef,
}: SocialPostSceneRendererProps) {
  useSocialPostSceneFonts(brandkit);
  const imageAssetByBlockId = new Map(linkedAssets.map((asset) => [asset.blockId, asset] as const));
  const sortedBlocks = getVisibleSortedSocialPostSceneBlocks(plan.scene.blocks);

  return (
    <Stage
      ref={stageRef}
      width={plan.scene.canvas.width * scale}
      height={plan.scene.canvas.height * scale}
      listening={listening}
      className={className}
    >
      <Layer listening={listening}>
        <KonvaRect
          x={0}
          y={0}
          width={plan.scene.canvas.width * scale}
          height={plan.scene.canvas.height * scale}
          fill={plan.scene.canvas.backgroundFill}
          listening={false}
        />
        {sortedBlocks.map((block) => (
          <SocialPostSceneStaticBlock
            key={block.id}
            block={block}
            context={{ scale, imageAssetByBlockId, brandkit, logoAssets, listening }}
          />
        ))}
      </Layer>
    </Stage>
  );
}

export function SocialPostSceneBlockContent({
  block,
  scale = 1,
  imageAssetByBlockId,
  brandkit,
  logoAssets = [],
  listening = true,
}: SocialPostSceneBlockContentProps) {
  return renderBlockContent(block, { scale, imageAssetByBlockId, brandkit, logoAssets, listening });
}

function SocialPostSceneStaticBlock({
  block,
  context,
}: {
  block: SceneBlock;
  context: SceneRenderContext;
}) {
  return (
    <KonvaGroup
      x={block.x * context.scale}
      y={block.y * context.scale}
      rotation={block.rotation}
      opacity={block.opacity}
      listening={context.listening}
    >
      {renderBlockContent(block, context)}
    </KonvaGroup>
  );
}

function renderBlockContent(block: SceneBlock, context: SceneRenderContext) {
  switch (block.type) {
    case "rect":
      return (
        <KonvaRect
          {...toKonvaRectNodeProps(block, context.scale, "local")}
          listening={context.listening}
        />
      );
    case "text":
      return <TextBlock block={block} context={context} />;
    case "image":
      return (
        <ImageBlock
          block={block}
          signedUrl={context.imageAssetByBlockId.get(block.id)?.signedUrl ?? null}
          scale={context.scale}
          listening={context.listening}
        />
      );
    case "logo": {
      const logoAsset = resolveSocialPostSceneLogoAsset({
        block,
        brandkit: context.brandkit,
        logoAssets: context.logoAssets,
      });
      return (
        <ImageBlock
          block={block}
          signedUrl={logoAsset?.signedUrl ?? null}
          scale={context.scale}
          listening={context.listening}
          missing="empty"
        />
      );
    }
    case "shape":
      return <ShapeBlock block={block} scale={context.scale} listening={context.listening} />;
    case "frame":
      return <FrameBlock block={block} scale={context.scale} listening={context.listening} />;
  }
}

function TextBlock({
  block,
  context,
}: {
  block: Extract<SceneBlock, { type: "text" }>;
  context: SceneRenderContext;
}) {
  const props = resolveSocialPostSceneTextNodeProps(
    block,
    context.scale,
    createCanvasTextMeasurer(block.fontFamily, block.fontStyle, block.fontSize * context.scale),
  );
  return <KonvaText {...props} listening={context.listening} />;
}

function ImageBlock({
  block,
  signedUrl,
  scale,
  listening,
  missing = "placeholder",
}: {
  block: Extract<SceneBlock, { type: "image" | "logo" }>;
  signedUrl: string | null;
  scale: number;
  listening: boolean;
  missing?: "placeholder" | "empty";
}) {
  const image = useLoadedImage(signedUrl);
  const frameProps = toKonvaImageFrameProps(block, scale, "local");
  const imageLayout = image
    ? getSceneImageFitLayout({
        imageWidth: image.naturalWidth,
        imageHeight: image.naturalHeight,
        frameX: frameProps.x,
        frameY: frameProps.y,
        frameWidth: frameProps.width,
        frameHeight: frameProps.height,
        fit: block.fit,
      })
    : null;

  if (!image) {
    return missing === "placeholder" ? (
      <KonvaRect
        x={frameProps.x}
        y={frameProps.y}
        width={frameProps.width}
        height={frameProps.height}
        fill="rgba(15, 23, 42, 0.08)"
        cornerRadius={frameProps.cornerRadius}
        opacity={frameProps.opacity}
        rotation={frameProps.rotation}
        listening={listening}
      />
    ) : null;
  }

  return (
    <KonvaImage
      x={imageLayout?.x ?? frameProps.x}
      y={imageLayout?.y ?? frameProps.y}
      width={imageLayout?.width ?? frameProps.width}
      height={imageLayout?.height ?? frameProps.height}
      image={image}
      crop={imageLayout?.crop}
      opacity={frameProps.opacity}
      rotation={frameProps.rotation}
      cornerRadius={frameProps.cornerRadius}
      listening={listening}
    />
  );
}

function ShapeBlock({
  block,
  scale,
  listening,
}: {
  block: Extract<SceneBlock, { type: "shape" }>;
  scale: number;
  listening: boolean;
}) {
  const width = block.width * scale;
  const height = block.height * scale;
  const centerX = width / 2;
  const centerY = height / 2;
  const common = {
    fill: block.fill,
    stroke: block.stroke,
    strokeWidth: block.strokeWidth * scale,
    opacity: 1,
    rotation: 0,
    dash: block.dash,
    lineCap: block.lineCap,
    lineJoin: block.lineJoin,
    listening,
  } as const;

  if (block.shapeType === "ellipse") {
    return (
      <KonvaEllipse x={centerX} y={centerY} radiusX={width / 2} radiusY={height / 2} {...common} />
    );
  }

  if (block.shapeType === "line") {
    const localPoints = block.points ?? [0, 0, block.width, block.height];
    return (
      <KonvaLine
        points={localPoints.map((value) => value * scale)}
        closed={block.closed}
        tension={block.tension}
        {...common}
      />
    );
  }

  if (block.shapeType === "polygon") {
    return (
      <KonvaRegularPolygon
        x={centerX}
        y={centerY}
        sides={block.sides ?? 6}
        radius={block.radius ? block.radius * scale : Math.min(width, height) / 2}
        {...common}
      />
    );
  }

  if (block.shapeType === "star") {
    return (
      <KonvaStar
        x={centerX}
        y={centerY}
        numPoints={block.sides ?? 5}
        innerRadius={block.innerRadius ? block.innerRadius * scale : Math.min(width, height) / 4}
        outerRadius={block.outerRadius ? block.outerRadius * scale : Math.min(width, height) / 2}
        {...common}
      />
    );
  }

  if (block.shapeType === "ring") {
    return (
      <KonvaRing
        x={centerX}
        y={centerY}
        innerRadius={block.innerRadius ? block.innerRadius * scale : Math.min(width, height) / 4}
        outerRadius={block.outerRadius ? block.outerRadius * scale : Math.min(width, height) / 2}
        {...common}
      />
    );
  }

  if (block.shapeType === "arc") {
    return (
      <KonvaArc
        x={centerX}
        y={centerY}
        innerRadius={block.innerRadius ? block.innerRadius * scale : Math.min(width, height) / 4}
        outerRadius={block.outerRadius ? block.outerRadius * scale : Math.min(width, height) / 2}
        angle={block.angle ?? 120}
        clockwise={block.clockwise}
        {...common}
      />
    );
  }

  return (
    <KonvaPath
      x={0}
      y={0}
      scaleX={scale}
      scaleY={scale}
      data={block.pathData ?? "M0 0"}
      {...common}
    />
  );
}

function FrameBlock({
  block,
  scale,
  listening,
}: {
  block: Extract<SceneBlock, { type: "frame" }>;
  scale: number;
  listening: boolean;
}) {
  return (
    <>
      {block.fill ? (
        <KonvaRect
          x={0}
          y={0}
          width={block.width * scale}
          height={block.height * scale}
          fill={block.fill}
          opacity={1}
          rotation={0}
          cornerRadius={block.cornerRadius * scale}
          listening={listening}
        />
      ) : null}
      <KonvaRect
        x={block.insetPadding * scale}
        y={block.insetPadding * scale}
        width={Math.max(0, block.width * scale - block.insetPadding * scale * 2)}
        height={Math.max(0, block.height * scale - block.insetPadding * scale * 2)}
        stroke={block.stroke}
        strokeWidth={block.strokeWidth * scale}
        opacity={1}
        rotation={0}
        cornerRadius={block.cornerRadius * scale}
        listening={listening}
      />
    </>
  );
}

function useLoadedImage(url: string | null): HTMLImageElement | null {
  const [image, setImage] = useState<HTMLImageElement | null>(null);

  useEffect(() => {
    if (!url) {
      setImage(null);
      return;
    }

    const img = new window.Image();
    img.crossOrigin = "anonymous";
    const onLoad = () => setImage(img);
    const onError = () => setImage(null);
    img.addEventListener("load", onLoad);
    img.addEventListener("error", onError);
    img.src = url;

    return () => {
      img.removeEventListener("load", onLoad);
      img.removeEventListener("error", onError);
    };
  }, [url]);

  return image;
}

function useSocialPostSceneFonts(brandkit: SocialPostSceneRendererBrandkit | null): void {
  useEffect(() => {
    if (!brandkit || typeof document === "undefined") {
      return;
    }

    let cancelled = false;

    const load = async () => {
      for (const font of brandkit.fonts) {
        if (cancelled) {
          return;
        }

        if (font.source === "brand_confirmed") {
          const face = new FontFace(font.family, `url(${font.loadUrl})`);
          try {
            const loaded = await face.load();
            document.fonts.add(loaded);
          } catch {
            // Best effort; the canvas falls back if the font cannot be fetched.
          }
          continue;
        }

        if (!document.querySelector(`link[data-font-url="${font.loadUrl}"]`)) {
          const link = document.createElement("link");
          link.rel = "stylesheet";
          link.href = font.loadUrl;
          link.dataset.fontUrl = font.loadUrl;
          document.head.append(link);
        }

        const weight = Number.parseInt(font.selectedVariant ?? "", 10);

        try {
          await document.fonts.load(
            `${Number.isFinite(weight) ? weight : 400} 16px ${toCssFontFamily(font.family)}`,
          );
        } catch {
          // Best effort; the canvas falls back if the font cannot be fetched.
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [brandkit]);
}

let textMeasureCanvas: HTMLCanvasElement | null = null;

function createCanvasTextMeasurer(fontFamily: string, fontStyle: string, fontSize: number) {
  return (value: string) => {
    if (typeof document === "undefined") {
      return value.length * fontSize * 0.5;
    }

    textMeasureCanvas = textMeasureCanvas ?? document.createElement("canvas");
    const context = textMeasureCanvas.getContext("2d");

    if (!context) {
      return value.length * fontSize * 0.5;
    }

    const style = fontStyle.includes("italic") ? "italic" : "normal";
    const weight = fontStyle.includes("bold") ? "700" : "400";
    context.font = `${style} ${weight} ${fontSize}px ${toCssFontFamily(fontFamily)}`;

    return context.measureText(value).width;
  };
}

function toCssFontFamily(fontFamily: string): string {
  return JSON.stringify(fontFamily);
}
